import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
// If you have your own Button/Input components, you can swap them in below.
// import Button from '../../components/ui/Button';
// import Input from '../../components/ui/Input';

const StepBadge = ({ index, active, done, label }) => (
  <div className="flex items-center gap-3">
    <div
      className={[
        "h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold",
        done ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300"
             : active ? "bg-indigo-600 text-white"
             : "bg-gray-100 text-gray-500"
      ].join(' ')}
      aria-current={active ? "step" : undefined}
    >
      {done ? "✓" : index + 1}
    </div>
    <span className={["text-sm", active ? "text-gray-900 font-medium" : "text-gray-500"].join(' ')}>
      {label}
    </span>
  </div>
);

const Divider = () => <div className="h-px bg-gray-200" />;

const Field = ({ label, hint, error, children, required }) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-gray-800">
      {label} {required && <span className="text-rose-600">*</span>}
    </label>
    {children}
    <div className="min-h-[20px]">
      {error ? (
        <p className="text-xs text-rose-600">{error}</p>
      ) : hint ? (
        <p className="text-xs text-gray-500">{hint}</p>
      ) : null}
    </div>
  </div>
);

const TextInput = (props) => (
  <input
    {...props}
    className={[
      "block w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none",
      "placeholder:text-gray-400",
      props.disabled ? "opacity-60 cursor-not-allowed" : "",
      props.error
        ? "border-rose-300 focus:ring-2 focus:ring-rose-200"
        : "border-gray-300 focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
    ].join(" ")}
  />
);

const Select = ({ options = [], ...props }) => (
  <select
    {...props}
    className={[
      "block w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none",
      "border-gray-300 focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
    ].join(" ")}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={[
      "relative inline-flex h-6 w-11 items-center rounded-full transition",
      checked ? "bg-indigo-600" : "bg-gray-300"
    ].join(" ")}
    role="switch"
    aria-checked={checked}
  >
    <span
      className={[
        "inline-block h-5 w-5 transform rounded-full bg-white transition",
        checked ? "translate-x-5" : "translate-x-1"
      ].join(" ")}
    />
    {label && <span className="sr-only">{label}</span>}
  </button>
);

/* ----------------------------- MAIN COMPONENT ----------------------------- */

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading } = useAuth();

  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);

  const [form, setForm] = useState({
    company_name: "",
    company_size: "1-10",
    hiring_goal: "Faster pipeline & scheduling",
    role_title: "",
    role_type: "Full-time",
    role_location: "",
    notify_email: "",
    enable_ai_screen: true,
    enable_calendar_sync: true,
    enable_analytics: true,
    ats_source: "Spreadsheet / Manual",
  });

  const [errors, setErrors] = useState({});

  // Prefill from existing profile/org if available
  useEffect(() => {
    if (user && userProfile) {
      setForm((f) => ({
        ...f,
        company_name: f.company_name || userProfile.org_name || "",
        notify_email: f.notify_email || user.email || "",
      }));
    }
  }, [user, userProfile]);

  const steps = useMemo(
    () => [
      { key: "company", label: "Company" },
      { key: "role", label: "First Role" },
      { key: "integrations", label: "Integrations" },
      { key: "preferences", label: "Preferences" },
      { key: "review", label: "Review & Finish" },
    ],
    []
  );

  const current = steps[step];

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!form.company_name?.trim()) e.company_name = "Company name is required.";
      if (!form.company_size) e.company_size = "Choose a size.";
    }
    if (step === 1) {
      if (!form.role_title?.trim()) e.role_title = "Role title is required.";
      if (!form.role_location?.trim()) e.role_location = "Add a location (Remote is OK).";
    }
    if (step === 3) {
      if (!form.notify_email?.trim() || !/\S+@\S+\.\S+/.test(form.notify_email))
        e.notify_email = "Provide a valid notification email.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onNext = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const onBack = () => setStep((s) => Math.max(s - 1, 0));

  const saveAndFinish = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // 1) Persist onboarding selections as needed (org + first role “draft”)
      const orgUpdates = {
        name: form.company_name.trim(),
        size_band: form.company_size,
        ats_source: form.ats_source,
        onboarding_completed_at: new Date().toISOString(),
      };

      // If your schema is different, adjust these writes accordingly.
      if (userProfile?.org_id) {
        await supabase.from('orgs').update(orgUpdates).eq('id', userProfile.org_id);
      }

      // Create a draft job/role record (optional)
      if (userProfile?.org_id && form.role_title?.trim()) {
        await supabase.from('jobs').insert({
          org_id: userProfile.org_id,
          title: form.role_title.trim(),
          job_type: form.role_type,
          location: form.role_location.trim(),
          status: 'draft',
          created_by: user?.id || null,
        });
      }

      // Save user preferences (optional table)
      await supabase.from('user_preferences').upsert({
        id: user?.id,
        notify_email: form.notify_email.trim(),
        ai_screen_enabled: form.enable_ai_screen,
        calendar_sync_enabled: form.enable_calendar_sync,
        analytics_enabled: form.enable_analytics,
      });

      // 2) Navigate into the app 🎉
      navigate('/app', { replace: true });
    } catch (err) {
      console.error('Onboarding save failed:', err);
      alert('We could not finish onboarding right now. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50">
        <div className="animate-spin h-12 w-12 rounded-full border-b-2 border-indigo-600" />
      </div>
    );
  }

  /* ------------------------------- STEP VIEWS ------------------------------ */

  const CompanyStep = () => (
    <div className="space-y-6">
      <Field label="Company name" required error={errors.company_name}>
        <TextInput
          placeholder="Acme Inc."
          value={form.company_name}
          onChange={(e) => setForm({ ...form, company_name: e.target.value })}
          error={errors.company_name}
        />
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Team size" required error={errors.company_size}>
          <Select
            value={form.company_size}
            onChange={(e) => setForm({ ...form, company_size: e.target.value })}
            options={[
              { value: "1-10", label: "1–10" },
              { value: "11-50", label: "11–50" },
              { value: "51-200", label: "51–200" },
              { value: "200+", label: "200+" },
            ]}
          />
        </Field>
        <Field label="Main hiring goal" hint="This helps us tailor defaults.">
          <Select
            value={form.hiring_goal}
            onChange={(e) => setForm({ ...form, hiring_goal: e.target.value })}
            options={[
              { value: "Faster pipeline & scheduling", label: "Faster pipeline & scheduling" },
              { value: "Collaborate with team", label: "Collaborate with team" },
              { value: "Better analytics", label: "Better analytics" },
              { value: "All of the above", label: "All of the above" },
            ]}
          />
        </Field>
      </div>

      <Field
        label="Where are you coming from?"
        hint="Optional — we can import later."
      >
        <Select
          value={form.ats_source}
          onChange={(e) => setForm({ ...form, ats_source: e.target.value })}
          options={[
            { value: "Spreadsheet / Manual", label: "Spreadsheet / Manual" },
            { value: "Greenhouse", label: "Greenhouse" },
            { value: "Lever", label: "Lever" },
            { value: "Other ATS", label: "Other ATS" },
          ]}
        />
      </Field>
    </div>
  );

  const RoleStep = () => (
    <div className="space-y-6">
      <Field label="First role title" required error={errors.role_title}>
        <TextInput
          placeholder="Software Engineer"
          value={form.role_title}
          onChange={(e) => setForm({ ...form, role_title: e.target.value })}
          error={errors.role_title}
        />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Employment type">
          <Select
            value={form.role_type}
            onChange={(e) => setForm({ ...form, role_type: e.target.value })}
            options={[
              { value: "Full-time", label: "Full-time" },
              { value: "Part-time", label: "Part-time" },
              { value: "Contract", label: "Contract" },
              { value: "Internship", label: "Internship" },
            ]}
          />
        </Field>
        <Field label="Location" required error={errors.role_location}>
          <TextInput
            placeholder="Remote (US), New York, London…"
            value={form.role_location}
            onChange={(e) => setForm({ ...form, role_location: e.target.value })}
            error={errors.role_location}
          />
        </Field>
      </div>
    </div>
  );

  const IntegrationsStep = () => (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 p-4 sm:p-6 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">AI Screening</h4>
            <p className="text-sm text-gray-600">Auto-score candidates against your role.</p>
          </div>
          <Toggle
            checked={form.enable_ai_screen}
            onChange={(v) => setForm({ ...form, enable_ai_screen: v })}
            label="Enable AI Screening"
          />
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 p-4 sm:p-6 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Calendar Sync</h4>
            <p className="text-sm text-gray-600">Invite candidates with one click.</p>
          </div>
          <Toggle
            checked={form.enable_calendar_sync}
            onChange={(v) => setForm({ ...form, enable_calendar_sync: v })}
            label="Enable Calendar Sync"
          />
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 p-4 sm:p-6 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Analytics</h4>
            <p className="text-sm text-gray-600">Track time-to-hire and funnel drop-offs.</p>
          </div>
          <Toggle
            checked={form.enable_analytics}
            onChange={(v) => setForm({ ...form, enable_analytics: v })}
            label="Enable Analytics"
          />
        </div>
      </div>
    </div>
  );

  const PreferencesStep = () => (
    <div className="space-y-6">
      <Field
        label="Notifications email"
        required
        error={errors.notify_email}
        hint="We’ll send updates about new candidates and interviews."
      >
        <TextInput
          placeholder="you@company.com"
          value={form.notify_email}
          onChange={(e) => setForm({ ...form, notify_email: e.target.value })}
          error={errors.notify_email}
          type="email"
        />
      </Field>

      <div className="rounded-2xl border border-gray-200 p-4 sm:p-6 bg-gradient-to-br from-indigo-50 to-white">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Default pipeline</h4>
        <p className="text-sm text-gray-600">
          We’ll start you with a best-practice funnel (Applied → Screen → Interview → Offer → Hired). You can customize it later.
        </p>
      </div>
    </div>
  );

  const ReviewStep = () => (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 p-4 sm:p-6 bg-white">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Company</h4>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div><dt className="text-gray-500">Name</dt><dd className="text-gray-900">{form.company_name || "—"}</dd></div>
          <div><dt className="text-gray-500">Team Size</dt><dd className="text-gray-900">{form.company_size}</dd></div>
          <div><dt className="text-gray-500">Goal</dt><dd className="text-gray-900">{form.hiring_goal}</dd></div>
          <div><dt className="text-gray-500">Source</dt><dd className="text-gray-900">{form.ats_source}</dd></div>
        </dl>
      </div>

      <div className="rounded-2xl border border-gray-200 p-4 sm:p-6 bg-white">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">First Role</h4>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div><dt className="text-gray-500">Title</dt><dd className="text-gray-900">{form.role_title || "—"}</dd></div>
          <div><dt className="text-gray-500">Type</dt><dd className="text-gray-900">{form.role_type}</dd></div>
          <div><dt className="text-gray-500">Location</dt><dd className="text-gray-900">{form.role_location || "—"}</dd></div>
        </dl>
      </div>

      <div className="rounded-2xl border border-gray-200 p-4 sm:p-6 bg-white">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Integrations & Preferences</h4>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div><dt className="text-gray-500">AI Screening</dt><dd className="text-gray-900">{form.enable_ai_screen ? "Enabled" : "Disabled"}</dd></div>
          <div><dt className="text-gray-500">Calendar Sync</dt><dd className="text-gray-900">{form.enable_calendar_sync ? "Enabled" : "Disabled"}</dd></div>
          <div><dt className="text-gray-500">Analytics</dt><dd className="text-gray-900">{form.enable_analytics ? "Enabled" : "Disabled"}</dd></div>
          <div><dt className="text-gray-500">Notify Email</dt><dd className="text-gray-900">{form.notify_email || "—"}</dd></div>
        </dl>
      </div>
    </div>
  );

  /* --------------------------------- LAYOUT -------------------------------- */

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b bg-white">
        <div className="mx-auto max-w-6xl px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-indigo-600 grid place-items-center text-white font-bold">I</div>
            <span className="font-semibold text-gray-900">Intime</span>
          </div>
          <div className="text-xs text-gray-500">Onboarding</div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-8">
        {/* Left: Stepper */}
        <aside className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <div className="space-y-5">
              {steps.map((s, i) => (
                <StepBadge
                  key={s.key}
                  index={i}
                  label={s.label}
                  active={i === step}
                  done={i < step}
                />
              ))}
            </div>
            <div className="mt-6">
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-2 bg-indigo-600 transition-all"
                  style={{ width: `${((step + 1) / steps.length) * 100}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Step {step + 1} of {steps.length}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-indigo-50 p-4">
            <h4 className="text-sm font-semibold text-indigo-900 mb-1">Tip</h4>
            <p className="text-sm text-indigo-900/80">
              You can tweak all of these settings later in Settings → Organization.
            </p>
          </div>
        </aside>

        {/* Right: Card */}
        <section className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {current.label}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {step === 0 && "Tell us a little about your company so we can tailor defaults."}
                {step === 1 && "Set up your first role so your pipeline is ready to go."}
                {step === 2 && "Toggle helpful automations and integrations."}
                {step === 3 && "Choose preferences for notifications and pipeline defaults."}
                {step === 4 && "Review your details before finishing onboarding."}
              </p>
            </div>

            <Divider />

            <div className="mt-6">
              {step === 0 && <CompanyStep />}
              {step === 1 && <RoleStep />}
              {step === 2 && <IntegrationsStep />}
              {step === 3 && <PreferencesStep />}
              {step === 4 && <ReviewStep />}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={onBack}
              disabled={step === 0 || saving}
              className={[
                "px-4 py-2 text-sm rounded-xl border transition",
                step === 0 || saving
                  ? "text-gray-400 border-gray-200 cursor-not-allowed"
                  : "text-gray-700 border-gray-300 hover:bg-gray-50"
              ].join(" ")}
            >
              Back
            </button>

            {step < steps.length - 1 ? (
              <button
                type="button"
                onClick={onNext}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 focus:ring-2 focus:ring-indigo-200 disabled:opacity-60"
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={saveAndFinish}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 focus:ring-2 focus:ring-emerald-200 disabled:opacity-60"
              >
                {saving ? "Finishing…" : "Finish & Go to App"}
              </button>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Onboarding;
